import InputField from "../components/common/InputField";
import { FormikProps } from "formik";
import { ProjectDataSchema } from "./projectDataSchema";
import { z } from "zod";
import Select from "react-select";
import { useCallback, useState } from "react";
import Trash from "../assest/svg/Trash.svg";

type Props = {
  formik: FormikProps<z.infer<typeof ProjectDataSchema>>;
  showDependency?: boolean; // Changed Boolean to boolean for consistency
};

function PhaseAndTask({ formik, showDependency }: Props) {
  const [openPhaseInput, setOpenPhaseInput] = useState<number | null>(null);
  const [openPhase, setOpenPhase] = useState<number | null>(0); // Keeps the first phase open by default
  const [phaseRemove, setPhaseRemove] = useState<number | undefined>();
  // State to hold the original task name when editing, to facilitate predecessor updates
  const [editingTaskName, setEditingTaskName] = useState<{
    phaseIndex: number;
    taskIndex: number;
    originalName: string;
  } | null>(null);

  /**
   * Helper function to get the name of the last task from the phase immediately preceding the given phaseIndex.
   * @param currentPhaseIndex The index of the phase for which we want to find the predecessor.
   * @returns The task name or undefined if no such task exists.
   */
  const getLastTaskOfPreviousPhase = useCallback(
    (currentPhaseIndex: number): string | undefined => {
      if (currentPhaseIndex > 0) {
        const prevPhase = formik.values.phases[currentPhaseIndex - 1];
        if (prevPhase && prevPhase.tasks.length > 0) {
          return prevPhase.tasks[prevPhase.tasks.length - 1].taskName;
        }
      }
      return undefined;
    },
    [formik.values.phases]
  );

  /**
   * Helper function to get the name of the last task within the specified phase.
   * @param currentPhaseIndex The index of the phase to check.
   * @returns The task name or undefined if no tasks exist in that phase.
   */
  const getLastTaskOfCurrentPhase = useCallback(
    (currentPhaseIndex: number): string | undefined => {
      const currentPhase = formik.values.phases[currentPhaseIndex];
      if (currentPhase && currentPhase.tasks.length > 0) {
        return currentPhase.tasks[currentPhase.tasks.length - 1].taskName;
      }
      return undefined;
    },
    [formik.values.phases]
  );

  const addPhase = () => {
    const newPhaseNumber = formik.values.phases.length + 1;
    // Get the last task from the phase that will precede the new phase (i.e., the current last phase)
    const defaultPredecessor = getLastTaskOfPreviousPhase(
      formik.values.phases.length
    );

    formik.setFieldValue("phases", [
      ...formik.values.phases,
      {
        phaseName: `Nouvelle phase ${newPhaseNumber}`,
        tasks: [
          {
            taskName: `tâche 1_phase_${newPhaseNumber}`,
            duration: 1,
            milestone: false, // Default milestone to false
            predecessorsList: defaultPredecessor ? [defaultPredecessor] : [], // Set default predecessor
          },
        ],
      },
    ]);
    // Set the newly added phase as open and make its name editable by default
    setOpenPhase(formik.values.phases.length); // The new phase's index is `formik.values.phases.length` after `setFieldValue`
    setOpenPhaseInput(formik.values.phases.length);
  };

  const removePhase = (phaseIndex: number) => {
    const updatedPhases = [...formik.values.phases];
    updatedPhases.splice(phaseIndex, 1);
    formik.setFieldValue("phases", updatedPhases);
    setPhaseRemove(undefined);
    // If the removed phase was open, close it or open the first one
    if (openPhase === phaseIndex) {
      setOpenPhase(updatedPhases.length > 0 ? 0 : null);
    }
    // Adjust openPhaseInput if necessary
    if (openPhaseInput === phaseIndex) {
      setOpenPhaseInput(null);
    }
  };

  const addTask = (phaseIndex: number) => {
    const updatedPhases = [...formik.values.phases];
    const taskNumber = updatedPhases[phaseIndex].tasks.length + 1;
    const phaseNumber = phaseIndex + 1;

    let defaultPredecessor: string | undefined;

    // If there are existing tasks in this phase, the new task's predecessor is the last task in this phase.
    if (updatedPhases[phaseIndex].tasks.length > 0) {
      defaultPredecessor = getLastTaskOfCurrentPhase(phaseIndex);
    } else {
      // If this is the first task in this phase (e.g., tasks were removed, or new phase was just added),
      // its predecessor is the last task of the previous phase.
      defaultPredecessor = getLastTaskOfPreviousPhase(phaseIndex);
    }

    updatedPhases[phaseIndex].tasks.push({
      taskName: `tâche_${taskNumber}_phase_${phaseNumber}`,
      duration: 1,
      predecessorsList: defaultPredecessor ? [defaultPredecessor] : [],
      milestone: false,
    });

    formik.setFieldValue("phases", updatedPhases);
  };

  const removeTask = (phaseIndex: number, taskIndex: number) => {
    const updatedPhases = [...formik.values.phases];
    // Remove the task
    const removedTaskName = updatedPhases[phaseIndex].tasks[taskIndex].taskName;
    updatedPhases[phaseIndex].tasks.splice(taskIndex, 1);

    // Update predecessors of all other tasks that might have depended on the removed task
    const phasesAfterRemoval = updatedPhases.map((phase) => ({
      ...phase,
      tasks: phase.tasks.map((task) => {
        if (
          task.predecessorsList &&
          task.predecessorsList.includes(removedTaskName)
        ) {
          const newPredecessorsList = task.predecessorsList.filter(
            (pred) => pred !== removedTaskName
          );
          return { ...task, predecessorsList: newPredecessorsList };
        }
        return task;
      }),
    }));

    formik.setFieldValue("phases", phasesAfterRemoval);
  };

  /**
   * Updates the predecessors lists of all tasks in the form when a task is renamed.
   * @param oldName The original name of the task.
   * @param newName The new name of the task.
   */
  const updatePredecessorsOnTaskRename = useCallback(
    (oldName: string, newName: string) => {
      // Only update if names are different and newName is not empty (assuming Zod schema validation will prevent empty names)
      if (oldName === newName || !newName) return;

      const updatedPhases = formik.values.phases.map((phase) => ({
        ...phase,
        tasks: phase.tasks.map((task) => {
          if (
            task.predecessorsList &&
            task.predecessorsList.includes(oldName)
          ) {
            const newPredecessorsList = task.predecessorsList.map((pred) =>
              pred === oldName ? newName : pred
            );
            return { ...task, predecessorsList: newPredecessorsList };
          }
          return task;
        }),
      }));
      formik.setFieldValue("phases", updatedPhases);
    },
    [formik] // formik reference is stable, its values are accessed directly.
  );

  /**
   * Displays validation error messages for form fields.
   * @param phaseIndex The index of the phase.
   * @param key The field name within the phase or task.
   * @param taskIndex The index of the task (optional, for task-specific fields).
   * @returns A JSX element containing the error message or null.
   */
  const showError = useCallback(
    (
      phaseIndex: number,
      key: "phaseName" | "taskName" | "duration" | "milestone",
      taskIndex?: number
    ) => {
      let error: string | undefined;
      let touched: boolean | undefined;

      if (taskIndex !== undefined) {
        // Accessing errors/touched for a task field
        error = (formik.errors.phases?.[phaseIndex] as any)?.tasks?.[
          taskIndex
        ]?.[key];
        touched = (formik.touched.phases?.[phaseIndex] as any)?.tasks?.[
          taskIndex
        ]?.[key];
      } else {
        // Accessing errors/touched for a phase field (e.g., phaseName)
        error = (formik.errors.phases?.[phaseIndex] as any)?.[key];
        touched = (formik.touched.phases?.[phaseIndex] as any)?.[key];
      }

      return touched && error ? (
        <p className="text-red-500 text-xs">{error}</p>
      ) : null;
    },
    [formik.errors, formik.touched]
  );

  /**
   * Generates options for the Select component based on all existing tasks,
   * excluding the current task itself.
   * @param currentTaskName The name of the task for which predecessors are being selected.
   * @returns An array of options for the Select component.
   */
  const DependenciesTask = useCallback(
    (currentTaskName: string) => {
      const options: {
        value: string;
        label: string;
      }[] = [];
      formik.values.phases.forEach((p) => {
        p.tasks.forEach((t) => {
          if (currentTaskName !== t.taskName) {
            // Exclude the current task from its own predecessors
            options.push({ value: t.taskName, label: t.taskName });
          }
        });
      });
      return options;
    },
    [formik.values.phases] // Re-memoize if phases or tasks within phases change
  );

  /**
   * Checks if a task with the same name already exists in another phase or as another task in the same phase.
   * @param taskName The name of the task to check.
   * @param currentPhaseIndex The index of the current task's phase.
   * @param currentTaskIndex The index of the current task.
   * @returns True if a duplicate is found, false otherwise.
   */
  const findSameTask = useCallback(
    (taskName: string, currentPhaseIndex: number, currentTaskIndex: number) => {
      let value = false;
      formik.values.phases.forEach((p, pi) => {
        p.tasks.forEach((t, ti) => {
          if (
            taskName === t.taskName &&
            (pi !== currentPhaseIndex || ti !== currentTaskIndex)
          ) {
            value = true;
          }
        });
      });
      return value;
    },
    [formik.values.phases]
  );

  return (
    <>
      <div className="grid grid-cols-3 items-center mb-4">
        <h2 className="text-lg font-semibold col-span-1">
          {!showDependency
            ? "Définissez les phases, tâches et leurs durées​"
            : "Déterminez les dépendances​"}
        </h2>
        {/* Add phase button centered */}
        <div className="flex justify-center col-span-1">
          {!showDependency && (
            <button
              type="button"
              onClick={addPhase}
              className="px-3 py-1.5 rounded-xl font-medium text-xs bg-purple-600/10 dark:bg-purple-600/20 text-purple-600 dark:text-purple-300 border border-purple-500/30 hover:bg-purple-600/20 transition-all"
            >
              + Ajouter une phase
            </button>
          )}
        </div>
        {/* Empty column for balance */}
        <div className="col-span-1"></div>
      </div>

      <div
        className={`grid ${
          !showDependency ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"
        } gap-4 w-full`}
      >
        {formik.values.phases.map((phase, phaseIndex) => (
          <div
            key={phaseIndex}
            className="border border-slate-200 dark:border-white/10 p-4 rounded-2xl shadow-md bg-white dark:bg-[#161924] min-w-[280px] h-fit relative text-slate-800 dark:text-slate-100"
          >
            <div className="flex justify-between items-center p-2.5 bg-slate-100 dark:bg-white/5 rounded-xl">
              <span className="font-medium text-xs flex-1 mr-2">
                {!showDependency ? (
                  <InputField
                    name={`phases[${phaseIndex}].phaseName`}
                    placeholder="Nom de la phase"
                    value={formik.values.phases[phaseIndex].phaseName}
                    onChange={formik.handleChange}
                    autoFocus={true}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      setOpenPhaseInput(null);
                    }}
                    disabled={Boolean(showDependency)}
                  />
                ) : (
                  <div className="font-semibold text-slate-900 dark:text-white truncate">
                    {phase.phaseName}
                  </div>
                )}
                {showError(phaseIndex, "phaseName")}
              </span>
              <button
                type="button"
                onClick={() =>
                  setOpenPhase(phaseIndex === openPhase ? null : phaseIndex)
                }
                className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              >
                {phaseIndex === openPhase ? "▲" : "▼"}
              </button>
            </div>
            <div className="flex justify-between items-center mb-2 absolute top-2 right-2">
              {!showDependency &&
                phaseIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => setPhaseRemove(phaseIndex)}
                    className="p-1 text-red-400 hover:text-red-600 transition-colors text-xs"
                    title="Supprimer la phase"
                  >
                    ✖
                  </button>
                )}
            </div>
            {phaseIndex === openPhase && (
              <div>
                <div className="mt-3 space-y-2.5">
                  {phase.tasks.map((task, taskIndex) => (
                    <div
                      className="border border-slate-200 dark:border-white/10 rounded-xl bg-slate-50/60 dark:bg-white/5 p-2.5"
                      key={taskIndex}
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-end justify-between gap-2 w-full">
                          <div className="w-full">
                            {!showDependency ? (
                              <InputField
                                name={`phases[${phaseIndex}].tasks[${taskIndex}].taskName`}
                                placeholder="Nom de la tâche"
                                value={
                                  formik.values.phases[phaseIndex].tasks[
                                    taskIndex
                                  ].taskName
                                }
                                onChange={formik.handleChange}
                                onFocus={() => {
                                  setEditingTaskName({
                                    phaseIndex,
                                    taskIndex,
                                    originalName:
                                      formik.values.phases[phaseIndex].tasks[
                                        taskIndex
                                      ].taskName,
                                  });
                                }}
                                onBlur={(e) => {
                                  formik.handleBlur(e);
                                  if (
                                    editingTaskName &&
                                    editingTaskName.phaseIndex === phaseIndex &&
                                    editingTaskName.taskIndex === taskIndex
                                  ) {
                                    updatePredecessorsOnTaskRename(
                                      editingTaskName.originalName,
                                      e.target.value
                                    );
                                    setEditingTaskName(null);
                                  }
                                }}
                                className="!w-full"
                              />
                            ) : (
                              <div className="flex justify-between items-center text-xs font-medium">
                                <span className="text-slate-800 dark:text-slate-200">{task.taskName}</span>
                                {formik.values.phases[phaseIndex].tasks[
                                  taskIndex
                                ].milestone && (
                                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20">
                                    Jalon
                                  </span>
                                )}
                              </div>
                            )}
                            <span className="text-red-500 text-xs">
                              {Boolean(
                                findSameTask(
                                  task.taskName,
                                  phaseIndex,
                                  taskIndex
                                )
                              ) && task.taskName
                                ? `Une tâche nommée "${task.taskName}" existe déjà.`
                                : ""}
                            </span>
                            {showError(phaseIndex, "taskName", taskIndex)}
                          </div>
                          {!showDependency && (
                            <div className="flex gap-2 items-center">
                              <div className="flex flex-col items-center">
                                <label className="text-[10px] text-slate-400 mb-1">
                                  Jalon
                                </label>
                                <input
                                  name={`phases[${phaseIndex}].tasks[${taskIndex}].milestone`}
                                  type="checkbox"
                                  checked={
                                    formik.values.phases[phaseIndex].tasks[
                                      taskIndex
                                    ].milestone
                                  }
                                  onChange={(e) => {
                                    formik.setFieldValue(
                                      `phases[${phaseIndex}].tasks[${taskIndex}].milestone`,
                                      e.target.checked
                                    );
                                    if (e.target.checked) {
                                      formik.setFieldValue(
                                        `phases[${phaseIndex}].tasks[${taskIndex}].duration`,
                                        1
                                      );
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                  className="w-4 h-4 accent-purple-600 rounded"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="text-[10px] text-slate-400 mb-1">
                                  Durée (j)
                                </label>
                                <div className="flex items-center gap-1">
                                  <InputField
                                    name={`phases[${phaseIndex}].tasks[${taskIndex}].duration`}
                                    type="number"
                                    min={1}
                                    placeholder="Jours"
                                    value={task.duration}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={
                                      formik.values.phases[phaseIndex].tasks[
                                        taskIndex
                                      ].milestone
                                    }
                                    className="!w-16 text-center"
                                  />
                                  {taskIndex > 0 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeTask(phaseIndex, taskIndex)
                                      }
                                      className="text-red-400 hover:text-red-600 p-1 text-xs"
                                    >
                                      ✖
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {showDependency && (
                          <div className="mt-1">
                            <span className="text-[10px] text-slate-400 mb-1 block">Prédécesseurs :</span>
                            <Select
                              className="w-full text-xs"
                              options={DependenciesTask(task.taskName)}
                              placeholder={"Sélectionner un prédécesseur"}
                              value={(
                                formik.values.phases?.[phaseIndex]?.tasks?.[
                                  taskIndex
                                ]?.predecessorsList ?? []
                              ).map((e) => ({
                                value: e,
                                label: e,
                              }))}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `phases[${phaseIndex}].tasks[${taskIndex}].predecessorsList`,
                                  e.map((item) => item.value)
                                )
                              }
                              menuPlacement="auto"
                              isMulti
                              styles={{
                                control: (base, state) => {
                                  const isDark = document.body.classList.contains("dark");
                                  return {
                                    ...base,
                                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                                    borderColor: state.isFocused ? "#7c3aed" : isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1",
                                    borderRadius: "0.75rem",
                                    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed" : "none",
                                    color: isDark ? "#f8fafc" : "#0f172a",
                                  };
                                },
                                menu: (base) => {
                                  const isDark = document.body.classList.contains("dark");
                                  return {
                                    ...base,
                                    backgroundColor: isDark ? "#161924" : "#ffffff",
                                    borderRadius: "0.75rem",
                                    border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
                                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
                                    zIndex: 9999,
                                  };
                                },
                                option: (base, state) => {
                                  const isDark = document.body.classList.contains("dark");
                                  return {
                                    ...base,
                                    backgroundColor: state.isFocused
                                      ? isDark
                                        ? "rgba(124, 58, 237, 0.25)"
                                        : "rgba(124, 58, 237, 0.1)"
                                      : "transparent",
                                    color: isDark ? "#e2e8f0" : "#1e293b",
                                    cursor: "pointer",
                                  };
                                },
                                multiValue: (base) => {
                                  const isDark = document.body.classList.contains("dark");
                                  return {
                                    ...base,
                                    backgroundColor: isDark ? "rgba(6, 182, 212, 0.15)" : "rgba(6, 182, 212, 0.12)",
                                    borderRadius: "0.5rem",
                                    border: isDark ? "1px solid rgba(6, 182, 212, 0.3)" : "1px solid rgba(6, 182, 212, 0.4)",
                                  };
                                },
                                multiValueLabel: (base) => {
                                  const isDark = document.body.classList.contains("dark");
                                  return {
                                    ...base,
                                    color: isDark ? "#38bdf8" : "#0284c7",
                                    fontSize: "11px",
                                    fontWeight: "500",
                                  };
                                },
                                multiValueRemove: (base) => {
                                  const isDark = document.body.classList.contains("dark");
                                  return {
                                    ...base,
                                    color: isDark ? "#38bdf8" : "#0284c7",
                                    ":hover": {
                                      backgroundColor: "rgba(239, 68, 68, 0.2)",
                                      color: "#ef4444",
                                    },
                                  };
                                },
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {!showDependency && (
                    <button
                      type="button"
                      onClick={() => addTask(phaseIndex)}
                      className="px-3 py-1.5 rounded-xl font-medium text-xs bg-cyan-500/10 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all w-full"
                    >
                      + Ajouter une tâche
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {phaseRemove !== undefined && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white dark:bg-[#161924] border border-slate-200 dark:border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-3">
              <img src={Trash} alt="Supprimer" className="w-6 h-6 dark:invert" />
            </div>
            <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-1 font-display">
              Supprimer cette phase ?
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
              Cette action supprimera également toutes les tâches associées à cette phase.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-700 dark:text-slate-300 transition-colors"
                onClick={() => setPhaseRemove(undefined)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-xl text-xs font-medium bg-red-600 hover:bg-red-700 text-white shadow-md transition-colors"
                onClick={() => removePhase(phaseRemove)}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PhaseAndTask;
